import { addNode, appendWire, findNode, isObjectEndpoint } from '../graph/graphModel.js';
import {
    getHassBindableProperties,
    getHassNodePort,
    getHassObjectPorts,
    getHassTriggerEvents,
    HASS_PORT_KIND,
    isPortCompatible,
    SWITCH_ELSE_PORT,
} from '../Hasp/properties/v2/hass/index.js';
import { targetContractFromAction } from '../automation/serviceTargetContract.js';
import { createHassNode } from './hassNodeFactory.js';
import { HASS_COLUMN, HASS_ROW } from './hassLayout.js';

/**
 * Home Assistant flow graph of one page — the rules of the HA domain on top
 * of the generic graph model (graph/graphModel.js). Pure functions.
 *
 *   Entity ─▶ Binding ─▶ (UI object) ─▶ Trigger ─▶ [Condition] ─▶ Action ─▶ Entity (its target)
 *   Entity ─▶ (UI object): its Primary Entity
 *   Entity ─▶ Switch ─case▶ Binding: the binding applies in that case only
 *
 * UI objects are referenced by Designer uuid and looked up through a context
 * `{ getObject(uuid) }` that only resolves objects of this page.
 */

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const sameEndpoint = (a, b) => a.port === b.port &&
    (a.object !== undefined ? a.object === b.object : a.node !== undefined && a.node === b.node);

/** Wires ending at an endpoint, in wire order. */
export function getWiresInto(graph, endpoint) {
    return graph.wires.filter(wire => sameEndpoint(wire.to, endpoint));
}

/** Kind a wire carries, from its source end. */
function wireKind(graph, wire) {
    if (isObjectEndpoint(wire.from)) return HASS_PORT_KIND.OBJECT;
    return getHassNodePort(findNode(graph, wire.from.node), 'out', wire.from.port)?.provides ?? null;
}

/** Entity id of the entity node wired into `endpoint`, or '' when none is. */
function wiredEntityId(graph, endpoint) {
    const wire = getWiresInto(graph, endpoint).find(item => wireKind(graph, item) === HASS_PORT_KIND.ENTITY);
    return wire ? (findNode(graph, wire.from.node)?.data?.entityId ?? '') : '';
}

/** The Primary Entity wired to a UI object, or ''. */
export function getObjectPrimaryEntity(graph, objectUuid) {
    return wiredEntityId(graph, { object: objectUuid, port: 'in' });
}

/** UI objects a binding node drives, in wire order. */
export function getBindingTargets(graph, bindingId) {
    return graph.wires
        .filter(wire => wire.from.node === bindingId && wire.from.port === 'value' && isObjectEndpoint(wire.to))
        .map(wire => wire.to.object);
}

/** The entity a binding reads: the wired entity node's, else one kept in its data (e.g. a template source). */
export function getBindingEntityId(graph, binding) {
    return wiredEntityId(graph, { node: binding.id, port: 'entity' }) || (binding.data?.entityId ?? '');
}

/** The switch case gating a binding — `{ switchId, port }` — or null. */
export function getBindingCase(graph, bindingId) {
    const wire = getWiresInto(graph, { node: bindingId, port: 'case' })[0];
    return wire ? { switchId: wire.from.node, port: wire.from.port } : null;
}

/** The entity wired to a switch, or '' (it then reads each object's Primary Entity). */
export function getSwitchEntityId(graph, switchId) {
    return wiredEntityId(graph, { node: switchId, port: 'entity' });
}

/** Binding nodes a switch gates, in node order. */
export function getSwitchBindings(graph, switchId) {
    return graph.nodes.filter(node => node.kind === 'binding' && getBindingCase(graph, node.id)?.switchId === switchId);
}

/**
 * Ungated binding nodes driving a property the switch gates on the same
 * object: they apply there when no case matches, as the switch's Else.
 */
export function getSwitchFallbackBindings(graph, switchId) {
    const gated = getSwitchBindings(graph, switchId);
    return graph.nodes.filter(node => node.kind === 'binding' && node.data?.property && !getBindingCase(graph, node.id)
        && gated.some(other => other.data?.property === node.data.property
            && getBindingTargets(graph, other.id).some(uuid => getBindingTargets(graph, node.id).includes(uuid))));
}

/** Binding nodes driving a UI object, in node order. */
export function getObjectBindingNodes(graph, objectUuid) {
    return graph.nodes.filter(node => node.kind === 'binding' && getBindingTargets(graph, node.id).includes(objectUuid));
}

/** The UI object (uuid) a trigger listens to, or null. */
export function getTriggerSource(graph, triggerId) {
    const wire = getWiresInto(graph, { node: triggerId, port: 'source' })[0];
    return wire && isObjectEndpoint(wire.from) ? wire.from.object : null;
}

/** The entity whose state change fires a trigger (a state trigger), or ''. */
export function getTriggerEntityId(graph, triggerId) {
    return wiredEntityId(graph, { node: triggerId, port: 'source' });
}

/** Whether a trigger listens to an entity's state rather than to a UI object. */
export function isStateTrigger(graph, triggerId) {
    return getWiresInto(graph, { node: triggerId, port: 'source' }).some(wire => wireKind(graph, wire) === HASS_PORT_KIND.ENTITY);
}

/** Trigger nodes listening to a UI object, in node order. */
export function getObjectTriggers(graph, objectUuid) {
    return graph.nodes.filter(node => node.kind === 'trigger' && getTriggerSource(graph, node.id) === objectUuid);
}

/** Outgoing wires of a trigger (`run`) or condition (`then`), in execution order. */
export function getRunWires(graph, nodeId) {
    return graph.wires
        .filter(wire => wire.from.node === nodeId && (wire.from.port === 'run' || wire.from.port === 'then'))
        .sort(byOrder);
}

/** The trigger a condition branches from, or null. */
export function getConditionTrigger(graph, conditionId) {
    return getWiresInto(graph, { node: conditionId, port: 'in' })[0]?.from.node ?? null;
}

/**
 * The entity a condition compares: the wired entity node's, else its trigger
 * source's Primary Entity (or, for a state trigger, the trigger's entity), or ''.
 */
export function getConditionEntityId(graph, conditionId) {
    const wired = wiredEntityId(graph, { node: conditionId, port: 'entity' });
    if (wired) return wired;
    const triggerId = getConditionTrigger(graph, conditionId);
    if (!triggerId) return '';
    const uuid = getTriggerSource(graph, triggerId);
    return uuid ? getObjectPrimaryEntity(graph, uuid) : getTriggerEntityId(graph, triggerId);
}

/** Whether a condition node compares an entity's state or attribute. */
export function conditionReadsEntity(data) {
    return (data?.conditions ?? []).some(condition => [condition.left, condition.right].some(value => value?.kind === 'entity'));
}

/** Nodes running an action: triggers and conditions. */
export function getActionRunners(graph, actionId) {
    return getWiresInto(graph, { node: actionId, port: 'run' }).map(wire => wire.from.node);
}

/** Wires from an action to the entity nodes it acts on, in target order. */
export function getActionTargetWires(graph, actionId) {
    return graph.wires.filter(wire => wire.from.node === actionId && wire.from.port === 'target').sort(byOrder);
}

/** Target entity ids of an action, in target order. */
export function getActionTargets(graph, actionId) {
    return getActionTargetWires(graph, actionId)
        .map(wire => findNode(graph, wire.to.node)?.data?.entityId ?? '')
        .filter(Boolean);
}

/**
 * Whether target entities may be wired to an action. Known targetless
 * services (from the HA service catalog or legacy providers such as
 * openhasp/esphome) and non-service actions refuse; an unknown service
 * accepts, because an explicit target is kept at export.
 */
export function actionAcceptsTarget(data) {
    if (data?.type !== 'call_service') return false;
    const contract = targetContractFromAction(data);
    return contract.known || contract.legacy ? contract.entity : true;
}

/*
 * One property of one object takes the cases of one switch, each once, and
 * one ungated binding — which applies when no case matches, so not next to a
 * binding on the switch's Else.
 */
function casesClash(a, b) {
    if (!a && !b) return true;
    if (!a || !b) return (a ?? b).port === SWITCH_ELSE_PORT;
    return a.switchId !== b.switchId || a.port === b.port;
}

/** Another binding node already drives `property` on the object, in the same case (`gate`) or ungated. */
function propertyBound(graph, objectUuid, property, exceptNodeId, gate = getBindingCase(graph, exceptNodeId)) {
    return getObjectBindingNodes(graph, objectUuid)
        .some(node => node.id !== exceptNodeId && node.data?.property === property
            && casesClash(gate, getBindingCase(graph, node.id)));
}

/** Another trigger node already listens to `event` on the object. */
function eventTaken(graph, objectUuid, event, exceptNodeId) {
    return Boolean(event) && getObjectTriggers(graph, objectUuid)
        .some(node => node.id !== exceptNodeId && node.data?.event === event);
}

function resolveEnd(graph, endpoint, dir, context) {
    if (isObjectEndpoint(endpoint)) {
        const object = context.getObject?.(endpoint.object);
        if (!object) return { error: 'Object is not on this page' };
        const ports = getHassObjectPorts(object);
        const port = (dir === 'in' ? ports.inputs : ports.outputs).find(item => item.id === endpoint.port);
        if (!port) return { error: dir === 'in' ? 'This object takes no Home Assistant input' : 'Object emits no events' };
        return { port, object };
    }
    const node = findNode(graph, endpoint?.node);
    if (!node) return { error: dir === 'in' ? 'Target node does not exist' : 'Source node does not exist' };
    const port = getHassNodePort(node, dir, endpoint.port);
    return port ? { port, node } : { error: dir === 'in' ? 'Unknown input port' : 'Unknown output port' };
}

function incompatibleReason(output, input, target) {
    if (output.provides === HASS_PORT_KIND.VALUE) return 'A binding drives a property of an object';
    if (output.provides === HASS_PORT_KIND.TARGET) return 'An action targets an entity node';
    if (output.provides === HASS_PORT_KIND.CASE) return 'A switch case gates a binding';
    if (target.node?.kind === 'entity') return 'An entity is targeted by an action';
    if (output.provides === HASS_PORT_KIND.BRANCH && target.node?.kind === 'condition') {
        return 'Conditions connect directly to a trigger';
    }
    if (output.provides === HASS_PORT_KIND.OBJECT) return 'An object’s events go to a trigger';
    if (target.node?.kind === 'action' && input.id === 'run') return 'Actions run from a trigger or a condition';
    return 'These ports cannot be connected';
}

/**
 * Check a prospective wire. Static rules come from the port declarations in
 * the hass schema; this adds the rules that need graph state.
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function canConnect(graph, from, to, context = {}) {
    const fail = reason => ({ ok: false, reason });
    const target = resolveEnd(graph, to, 'in', context);
    if (target.error) return fail(target.error);
    const source = resolveEnd(graph, from, 'out', context);
    if (source.error) return fail(source.error);
    if (from.node !== undefined && from.node === to.node) return fail('Cannot connect a node to itself');
    if (!isPortCompatible(source.port, target.port)) return fail(incompatibleReason(source.port, target.port, target));
    const into = getWiresInto(graph, to);
    if (into.some(wire => sameEndpoint(wire.from, from))) return fail('Already connected');
    if (target.port.max !== undefined && into.length >= target.port.max) {
        return fail(target.node?.kind === 'trigger' ? 'Trigger already has a source' : 'Input is already connected');
    }
    const kind = source.port.provides;

    if (target.object && kind === HASS_PORT_KIND.ENTITY &&
        into.some(wire => wireKind(graph, wire) === HASS_PORT_KIND.ENTITY)) {
        return fail('This object already has a Primary Entity');
    }
    if (target.object && kind === HASS_PORT_KIND.VALUE) {
        const property = source.node.data?.property;
        if (property && !getHassBindableProperties(target.object).includes(property)) {
            return fail(`"${property}" cannot be bound on this object`);
        }
        if (property && propertyBound(graph, to.object, property, source.node.id)) {
            return fail(`"${property}" is already bound on this object`);
        }
    }
    if (target.node?.kind === 'trigger' && source.object) {
        const event = target.node.data?.event;
        const choices = getHassTriggerEvents(source.object);
        if (event && !choices.includes(event)) return fail(`Event "${event}" is not available for this object`);
        if (getObjectTriggers(graph, from.object).length >= choices.length) {
            return fail('This object has no unused events left');
        }
        if (eventTaken(graph, from.object, event, target.node.id)) {
            return fail(`This object already has a "${event}" trigger`);
        }
    }
    if (target.node?.kind === 'binding' && kind === HASS_PORT_KIND.CASE) {
        const property = target.node.data?.property;
        const gate = { switchId: from.node, port: from.port };
        if (property && getBindingTargets(graph, to.node).some(uuid => propertyBound(graph, uuid, property, to.node, gate))) {
            return fail(`"${property}" is already bound in this case`);
        }
    }
    if (source.node?.kind === 'action' && from.port === 'target' && !actionAcceptsTarget(source.node.data)) {
        return fail('This action takes no target');
    }
    return { ok: true };
}

const nextOrder = wires => (wires.length ? Math.max(...wires.map(wire => wire.order ?? 0)) + 1 : 0);

/** @returns {{ graph, wire } | { graph, error }} — `graph` is unchanged on error. */
export function connect(graph, from, to, context = {}, { id } = {}) {
    const check = canConnect(graph, from, to, context);
    if (!check.ok) return { graph, error: check.reason };
    let order;
    if (from.node !== undefined && (from.port === 'run' || from.port === 'then')) order = nextOrder(getRunWires(graph, from.node));
    else if (from.node !== undefined && from.port === 'target') order = nextOrder(getActionTargetWires(graph, from.node));
    return appendWire(graph, from, to, { id, order });
}

/** Event choices for a trigger: its source's events, with those already used by a sibling disabled. */
export function getTriggerEventChoices(graph, triggerId, context = {}) {
    const uuid = getTriggerSource(graph, triggerId);
    const object = uuid ? context.getObject?.(uuid) : null;
    return (object ? getHassTriggerEvents(object) : []).map(event => ({
        event, disabled: eventTaken(graph, uuid, event, triggerId),
    }));
}

/** Property choices for a binding: the bindable properties shared by its target objects. */
export function getBindingPropertyChoices(graph, bindingId, context = {}) {
    const objects = getBindingTargets(graph, bindingId).map(uuid => context.getObject?.(uuid)).filter(Boolean);
    if (objects.length === 0) return [];
    const [first, ...rest] = objects.map(getHassBindableProperties);
    const shared = first.filter(property => rest.every(list => list.includes(property)));
    return shared.map(property => ({
        property,
        disabled: getBindingTargets(graph, bindingId).some(uuid => propertyBound(graph, uuid, property, bindingId)),
    }));
}

/**
 * Structural diagnostics `{ severity, code, message, nodeId? | wireId? }`.
 * `error` marks inconsistent graph data; `warning` marks incomplete
 * authoring. Triggers without a source or event, and duplicate triggers, are
 * left out of the export (see hassCompiler); everything else exports as it
 * did from the HASSBuilder.
 */
export function validateHassGraph(graph, context = {}) {
    const diagnostics = [];
    const report = (severity, code, message, detail) => diagnostics.push({ severity, code, message, ...detail });
    const nodeIds = new Set(graph.nodes.map(node => node.id));
    for (const wire of graph.wires) {
        const missingNode = [wire.from, wire.to].some(end => !isObjectEndpoint(end) && !nodeIds.has(end.node));
        const missingObject = [wire.from, wire.to].some(end => isObjectEndpoint(end) && !context.getObject?.(end.object));
        if (missingNode) report('error', 'dangling-wire', 'Wire references a missing node', { wireId: wire.id });
        else if (missingObject) report('error', 'missing-object', 'Wire references an object that is not on this page', { wireId: wire.id });
    }
    const triggerKeys = new Map();
    for (const node of graph.nodes) {
        const at = { nodeId: node.id };
        switch (node.kind) {
        case 'entity':
            if (!node.data?.entityId) report('warning', 'entity-no-id', 'Select an entity', at);
            break;
        case 'binding': {
            const targets = getBindingTargets(graph, node.id);
            const property = node.data?.property;
            if (targets.length === 0) report('warning', 'binding-no-target', 'Binding drives no object', at);
            else if (!property) report('warning', 'binding-no-property', 'Select a property', at);
            for (const uuid of property ? targets : []) {
                const object = context.getObject?.(uuid);
                if (object && !getHassBindableProperties(object).includes(property)) {
                    report('warning', 'binding-unsupported', `"${property}" is not a bindable property of this object`, at);
                }
                if (propertyBound(graph, uuid, property, node.id)) {
                    report('warning', 'binding-duplicate', `"${property}" is bound more than once on this object`, at);
                }
            }
            break;
        }
        case 'trigger': {
            if (isStateTrigger(graph, node.id)) {
                if (!getTriggerEntityId(graph, node.id)) report('warning', 'trigger-no-source', 'Select the entity of this trigger', at);
                if (getRunWires(graph, node.id).length === 0) report('warning', 'trigger-no-actions', 'Trigger runs no actions', at);
                break;
            }
            const uuid = getTriggerSource(graph, node.id);
            const object = uuid ? context.getObject?.(uuid) : null;
            const event = node.data?.event;
            if (!object) report('warning', 'trigger-no-source', 'Trigger has no source object', at);
            else if (!event) report('warning', 'trigger-no-event', 'Trigger has no event selected', at);
            else {
                if (!getHassTriggerEvents(object).includes(event)) {
                    report('warning', 'trigger-unsupported', `Event "${event}" is not published by this object`, at);
                }
                const key = `${uuid}\u0000${event}`;
                if (triggerKeys.has(key)) report('error', 'trigger-duplicate', `Duplicate "${event}" trigger for this object`, at);
                else triggerKeys.set(key, node.id);
            }
            if (getRunWires(graph, node.id).length === 0) report('warning', 'trigger-no-actions', 'Trigger runs no actions', at);
            break;
        }
        case 'switch': {
            const bindings = getSwitchBindings(graph, node.id);
            if (bindings.length === 0) report('warning', 'switch-unused', 'Switch gates no binding', at);
            const unresolved = !getSwitchEntityId(graph, node.id) && bindings
                .flatMap(binding => getBindingTargets(graph, binding.id))
                .some(uuid => !getObjectPrimaryEntity(graph, uuid));
            if (unresolved) report('warning', 'switch-no-entity', 'Wire an entity, or give the objects a Primary Entity', at);
            break;
        }
        case 'condition':
            if (!getConditionTrigger(graph, node.id)) report('warning', 'condition-no-trigger', 'Condition is not connected to a trigger', at);
            if (getRunWires(graph, node.id).length === 0) report('warning', 'condition-no-actions', 'Condition runs no actions', at);
            if (conditionReadsEntity(node.data) && !getConditionEntityId(graph, node.id)) {
                report('warning', 'condition-no-entity', 'Wire an entity, or give the object a Primary Entity', at);
            }
            break;
        case 'action':
            if (getActionRunners(graph, node.id).length === 0) {
                report('warning', 'action-unconnected', 'Action is not connected to a trigger or condition', at);
            }
            if (getActionTargets(graph, node.id).length > 0 && !actionAcceptsTarget(node.data)) {
                report('warning', 'action-target-ignored', 'This action takes no target entity', at);
            }
            break;
        default:
        }
    }
    return diagnostics;
}

/**
 * The Home Assistant flow belonging to a set of UI objects, for copying:
 * the bindings driving them, the switches gating those and the triggers
 * listening to them, with the conditions and actions those triggers run,
 * plus every entity node feeding any of these (or the objects' Primary
 * Entity) — so a copy works on its own. Wires stay only between members; a binding's wires to other objects
 * are left behind.
 */
export function extractObjectsHass(graph, objectUuids) {
    const objects = new Set(objectUuids);
    const ids = new Set();
    for (const node of graph.nodes) {
        if (node.kind === 'binding' && getBindingTargets(graph, node.id).some(uuid => objects.has(uuid))) {
            ids.add(node.id);
            const gate = getBindingCase(graph, node.id);
            if (gate) ids.add(gate.switchId);
        }
        if (node.kind === 'trigger' && objects.has(getTriggerSource(graph, node.id))) ids.add(node.id);
    }
    for (const triggerId of [...ids].filter(id => findNode(graph, id)?.kind === 'trigger')) {
        for (const wire of getRunWires(graph, triggerId)) {
            ids.add(wire.to.node);
            for (const branch of getRunWires(graph, wire.to.node)) ids.add(branch.to.node);
        }
    }
    const inside = end => (isObjectEndpoint(end) ? objects.has(end.object) : ids.has(end.node));
    const isEntity = end => !isObjectEndpoint(end) && findNode(graph, end.node)?.kind === 'entity';
    // Entities feeding a member, and entities a member action acts on.
    const entities = graph.wires.flatMap(wire => [
        ...(isEntity(wire.from) && inside(wire.to) ? [wire.from.node] : []),
        ...(isEntity(wire.to) && wire.from.port === 'target' && inside(wire.from) ? [wire.to.node] : []),
    ]);
    entities.forEach(id => ids.add(id));
    return {
        nodes: structuredClone(graph.nodes.filter(node => ids.has(node.id))),
        wires: structuredClone(graph.wires.filter(wire => inside(wire.from) && inside(wire.to))),
    };
}

/**
 * A trigger's run wires as the export orders them (HomeAssistantGenerator
 * _buildEvents): its actions in wire order, and all its condition branches
 * as one `choose` — after the actions, or for a btnmatrix at the position of
 * its first branch.
 * @returns {Array<{ type: 'action', wire } | { type: 'choose', branches: wire[] }>}
 */
export function getTriggerRunLayout(graph, triggerId, { btnmatrix = false } = {}) {
    const wires = getRunWires(graph, triggerId);
    const isBranch = wire => findNode(graph, wire.to.node)?.kind === 'condition';
    const branches = wires.filter(isBranch);
    const choose = branches.length > 0 ? [{ type: 'choose', branches }] : [];
    if (!btnmatrix) return [...wires.filter(wire => !isBranch(wire)).map(wire => ({ type: 'action', wire })), ...choose];
    const items = [];
    for (const wire of wires) {
        if (!isBranch(wire)) items.push({ type: 'action', wire });
        else if (wire === branches[0]) items.push(...choose);
    }
    return items;
}

/** The wire ids of a run layout, in order (for reorderNodeOutputs). */
export function flattenRunLayout(items) {
    return items.flatMap(item => (item.type === 'choose' ? item.branches.map(wire => wire.id) : [item.wire.id]));
}

/**
 * Set an action's target entities to `entityIds`, in that order. An entity
 * node this action or another one already targets is reused; otherwise a new
 * one is placed right of the action. Pure; returns the new graph.
 */
export function setActionTargets(graph, actionId, entityIds) {
    const action = findNode(graph, actionId);
    if (!action) return graph;
    const current = getActionTargetWires(graph, actionId);
    let next = { ...graph, wires: graph.wires.filter(wire => !current.includes(wire)) };
    const targetedNodes = new Set(graph.wires.filter(wire => wire.from.port === 'target').map(wire => wire.to.node));
    const reusable = entityId => {
        const candidates = next.nodes.filter(node => node.kind === 'entity' && node.data?.entityId === entityId);
        return candidates.find(node => current.some(wire => wire.to.node === node.id))
            ?? candidates.find(node => targetedNodes.has(node.id))
            ?? null;
    };
    entityIds.forEach((entityId, index) => {
        let entity = reusable(entityId);
        if (!entity) {
            entity = createHassNode('entity', { x: action.x + HASS_COLUMN, y: action.y + index * HASS_ROW, preset: { entityId } });
            next = addNode(next, entity);
        }
        next = appendWire(next, { node: actionId, port: 'target' }, { node: entity.id, port: 'target' }, { order: index }).graph;
    });
    return next;
}
